<template>
  <div class="mt-4 space-y-5 divide-y divide-gray-900/10">
    <div class="grid grid-cols-1 gap-8 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">{{ kbStore.kbTypeTitle }}</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Specify name and description for the Knowledge Base</p>
        </div>
      </div>

      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
            <div class="sm:col-span-4">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">Name</label>
              <div class="mt-2">
                <div class="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    id="name"
                    v-model="specName"
                    type="text"
                    name="name"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Specify KB Name"
                  />
                </div>
              </div>
            </div>

            <InputTextarea v-model="specDescription" class="col-span-full" name="description" label="Description" placeholder="Provide brief description of the KB"></InputTextarea>

            <div class="col-span-full">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">Tags</label>
              <div class="text-xs text-gray-500">List of Tags that can be set on KB Documents</div>
              <StdTagInputEditor v-model="specTags" class="mt-2" :available-tags="kbStore.kbTags" @add-tag="onKbAddTag"></StdTagInputEditor>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Web Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Load data from the Web, for example from your website.</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup v-model="webSourceEnabled" :disabled="true" class="col-span-full" :name="propsDefs['webSourceEnabled'].title" :description="propsDefs['webSourceEnabled'].description">
              <div class="mt-4 grid max-w-2xl grid-cols-1 gap-x-6 gap-y-2 border-l-2 border-indigo-100 pl-4 sm:grid-cols-6">
                <InputTextarea
                  v-model="sitemaps"
                  class="col-span-full"
                  name="sitemaps"
                  :label="propsDefs['sitemaps'].title"
                  :description="propsDefs['sitemaps'].description"
                  :placeholder="propsDefs['sitemaps'].placeholder"
                  :rows="2"
                ></InputTextarea>

                <InputTextarea
                  v-model="urls"
                  class="col-span-full"
                  name="urls"
                  :label="propsDefs['urls'].title"
                  :description="propsDefs['urls'].description"
                  :placeholder="propsDefs['urls'].placeholder"
                  :rows="2"
                ></InputTextarea>
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>

    <div v-if="settingsStore.salesforceIntegrationEnabled" class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Salesforce Help Center Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Enable load data from your Salesforce Help Centers</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup
              v-model="salesforceHelpCenterSourceEnabled"
              class="col-span-full"
              :name="propsDefs['salesforceHelpCenterSourceEnabled'].title"
              :description="propsDefs['salesforceHelpCenterSourceEnabled'].description"
            >
              <div class="col-span-full mt-4 border-l-2 border-indigo-100 pl-4">
                <StdPropEditor v-model="salesforceHelpCenterRecordTypes" style="display: none" class="col-span-4 mt-2" :prop-def="propsDefs['salesforceHelpCenterRecordTypes']" />
                <MultiSelect v-model="hcRecordTypesSelected" :options="hcRecordTypes" placeholder="Select Record Types" :max-selected-labels="3" class="w-full" />
                <StdPropEditor v-model="salesforcePrivateArticlesEnabled" class="col-span-4 mt-2" :prop-def="propsDefs['salesforcePrivateArticlesEnabled']" />
              </div>
              <div class="col-span-full flex items-center justify-end gap-x-2 border-l-2 border-indigo-100 pl-4 pt-6">
                <Button label="Reload all" icon="pi pi-cloud-download" severity="secondary" :disabled="!hcRecordTypesSelected.length" @click="onDownloadSalesforceHelpCenters(true)" />
                <Button icon="pi pi-cloud-download" label="Load Updates" :disabled="!hcRecordTypesSelected.length" @click="onDownloadSalesforceHelpCenters(false)" />
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>
    <div v-if="settingsStore.shopifyIntegrationEnabled" class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Shopify Help Center Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Enable load data from your Shopify Help Centers</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup v-model="shopifyHelpCenterSourceEnabled" class="col-span-full" :name="propsDefs['shopifyHelpCenterSourceEnabled'].title" :description="propsDefs['shopifyHelpCenterSourceEnabled'].description">
              <div class="col-span-full mt-4 border-l-2 border-indigo-100 pl-4">
                <StdPropEditor v-model="shopifyHelpCenterRecordTypes" style="display: none" class="col-span-4 mt-2" :prop-def="propsDefs['shopifyHelpCenterRecordTypes']" />
                <MultiSelect v-model="shopifyRecordTypesSelected" :options="shopifyRecordTypes" placeholder="Select Record Types" :max-selected-labels="3" class="w-full" />
              </div>
              <div v-if="shopifyRecordTypesSelected.includes('Products')" class="col-span-full mt-4 border-l-2 border-indigo-100 pl-4">
                <StdPropEditor v-model="shopifyProductMetafieldDefinitions" style="display: none" class="col-span-4 mt-2" :prop-def="propsDefs['shopifyProductMetafieldDefinitions']" />
                <MultiSelect
                  v-model="shopifyProductMetafieldDefinitionsSelected"
                  :options="shopifyProducts"
                  option-label="name"
                  option-value="name"
                  :placeholder="loadingShopifyProducts ? '⏳ Loading...' : 'Select Product Metafield Definitions'"
                  class="w-full"
                />
              </div>
              <div class="col-span-full flex items-center justify-end gap-x-2 border-l-2 border-indigo-100 pl-4 pt-6">
                <Button label="Reload all" icon="pi pi-cloud-download" severity="secondary" :disabled="!shopifyRecordTypesSelected.length" @click="onDownloadShopifyHelpCenters(true)" />
                <Button icon="pi pi-cloud-download" label="Load Updates" :disabled="!shopifyRecordTypesSelected.length" @click="onDownloadShopifyHelpCenters(false)" />
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>

    <!-- [sv2] DO NOT CHECK settingsStore.zendeskIntegrationEnabled here. Loading from Zendesk HC can be done without enabling integration, via public API -->
    <div class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Zendesk Help Center Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Load data from your Zendesk Help Centers</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup v-model="zendeskHelpCenterSourceEnabled" class="col-span-full" :name="propsDefs['zendeskHelpCenterSourceEnabled'].title" :description="propsDefs['zendeskHelpCenterSourceEnabled'].description">
              <div class="col-span-full mt-4 border-l-2 border-indigo-100 pl-4">
                <StdPropEditor v-model="zendeskHelpCentersSubdomains" class="col-span-4 mt-2" :prop-def="propsDefs['zendeskHelpCentersSubdomains']" />
                <!-- [sv2] Only loading private articles requires zendesk integration -->
                <StdPropEditor v-if="settingsStore.zendeskIntegrationEnabled" v-model="zendeskPrivateArticlesEnabled" class="col-span-4 mt-2" :prop-def="propsDefs['zendeskPrivateArticlesEnabled']" />
              </div>
              <div class="col-span-full flex items-center justify-end gap-x-2 border-l-2 border-indigo-100 pl-4 pt-6">
                <Button label="Reload all" icon="pi pi-cloud-download" severity="secondary" @click="onDownloadZendeskHelpCenters(true)" />
                <Button icon="pi pi-cloud-download" label="Load Updates" @click="onDownloadZendeskHelpCenters(false)" />
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>
    <div v-if="settingsStore.kustomerIntegrationEnabled" class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Kustomer Help Center Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Load data from your Kustomer Help Centers</p>
        </div>
      </div>
      <div v-if="settingsStore.kustomerIntegrationEnabled" class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup v-model="kustomerHelpCenterSourceEnabled" class="col-span-full" :name="propsDefs['kustomerHelpCenterSourceEnabled'].title" :description="propsDefs['kustomerHelpCenterSourceEnabled'].description">
              <div class="col-span-full mt-4 border-l-2 border-indigo-100 pl-4">
                <StdPropEditor v-model="kustomerPrivateArticlesEnabled" class="col-span-4 mt-2" :prop-def="propsDefs['kustomerPrivateArticlesEnabled']" />
              </div>
              <div class="col-span-full flex items-center justify-end gap-x-2 pl-4 pt-6">
                <Button label="Reload all" icon="pi pi-cloud-download" severity="secondary" @click="onDownloadKustomerHelpCenters(true)" />
                <Button icon="pi pi-cloud-download" label="Load Updates" @click="onDownloadKustomerHelpCenters(false)" />
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>
    <div v-if="settingsStore.gorgiasIntegrationEnabled" class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Gorgias Help Center Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Load data from your Gorgias Help Centers</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup v-model="gorgiasHelpCenterSourceEnabled" class="col-span-full" :name="propsDefs['gorgiasHelpCenterSourceEnabled'].title" :description="propsDefs['gorgiasHelpCenterSourceEnabled'].description">
              <div class="col-span-full mt-4 border-l-2 border-indigo-100 pl-4">
                <StdPropEditor v-model="gorgiasHelpCentersSubdomains" class="col-span-4 mt-2" :prop-def="propsDefs['gorgiasHelpCentersSubdomains']" />
              </div>
              <div class="col-span-full flex items-center justify-end gap-x-2 border-l-2 border-indigo-100 pl-4 pt-6">
                <Button label="Reload all" icon="pi pi-cloud-download" severity="secondary" @click="onDownloadGorgiasHelpCenters(true)" />
                <Button icon="pi pi-cloud-download" label="Load Updates" @click="onDownloadGorgiasHelpCenters(false)" />
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>

    <div v-if="settingsStore.intercomIntegrationEnabled" class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Intercom Help Center Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Load data from your Intercom Help Centers</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup v-model="intercomHelpCenterSourceEnabled" class="col-span-full" :name="propsDefs['intercomHelpCenterSourceEnabled'].title" :description="propsDefs['intercomHelpCenterSourceEnabled'].description">
              <div class="col-span-full flex items-center justify-end gap-x-2 border-l-2 border-indigo-100 pl-4 pt-6">
                <Button label="Reload all" icon="pi pi-cloud-download" severity="secondary" @click="onDownloadIntercomHelpCenters(true)" />
                <Button icon="pi pi-cloud-download" label="Load Updates" @click="onDownloadIntercomHelpCenters(false)" />
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>

    <div v-if="settingsStore.confluenceIntegrationEnabled" class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Confluence Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Load data from your Confluence</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup
              v-model="confluenceHelpCenterSourceEnabled"
              class="col-span-full"
              :name="propsDefs['confluenceHelpCenterSourceEnabled'].title"
              :description="propsDefs['confluenceHelpCenterSourceEnabled'].description"
            >
              <div class="col-span-full mt-4 border-l-2 border-indigo-100 pl-4">
                <StdPropEditor v-model="confluenceSpaceKeys" class="col-span-4 mt-2" :prop-def="propsDefs['confluenceSpaceKeys']" />
                <StdPropEditor v-model="confluenceLabels" class="col-span-4 mt-2" :prop-def="propsDefs['confluenceLabels']" />
              </div>
              <div class="col-span-full flex items-center justify-end gap-x-2 border-l-2 border-indigo-100 pl-4 pt-6">
                <Button label="Reload all" icon="pi pi-cloud-download" severity="secondary" @click="onDownloadConfluenceHelpCenters(true)" />
                <Button icon="pi pi-cloud-download" label="Load Updates" @click="onDownloadConfluenceHelpCenters(false)" />
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>

    <div v-if="settingsStore.guruIntegrationEnabled" class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Guru Help Center Data</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Load data from your Guru Help Centers</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <ToggleGroup v-model="guruHelpCenterSourceEnabled" class="col-span-full" :name="propsDefs['guruHelpCenterSourceEnabled'].title" :description="propsDefs['guruHelpCenterSourceEnabled'].description">
              <div class="col-span-full mt-4 border-l-2 border-indigo-100 pl-4">
                <StdPropEditor v-model="guruHelpCentersTags" class="col-span-4 mt-2" :prop-def="propsDefs['guruHelpCentersTags']" />
                <StdPropEditor v-model="guruPrivateArticlesEnabled" class="col-span-4 mt-2" :prop-def="propsDefs['guruPrivateArticlesEnabled']" />
              </div>
              <div class="col-span-full flex items-center justify-end gap-x-2 border-l-2 border-indigo-100 pl-4 pt-6">
                <Button label="Reload all" icon="pi pi-cloud-download" severity="secondary" @click="onDownloadGuruHelpCenters(true)" />
                <Button icon="pi pi-cloud-download" label="Load Updates" @click="onDownloadGuruHelpCenters(false)" />
              </div>
            </ToggleGroup>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-8 pt-6 md:grid-cols-5">
      <div class="relative px-4 sm:px-0">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Advanced</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Show advanced options</p>
        </div>
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-4">
        <div class="px-4 py-2">
          <div class="grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-6">
            <div class="col-span-full">
              <ToggleGroup v-model="advancedEnabled" name="Advanced" description="">
                <StdPropEditor v-model="mode" class="col-span-4 mt-2" :prop-def="propsDefs['mode']" />
                <StdPropEditor v-model="embeddingModel" class="col-span-4 mt-4" :prop-def="propsDefs['embeddingModel']" />
                <StdPropEditor v-model="vectorStore" class="col-span-4 mt-4" :prop-def="propsDefs['vectorStore']" />
                <StdPropEditor v-model="splitSizeThreshold" class="col-span-4 mt-4" :prop-def="propsDefs['splitSizeThreshold']" />
                <StdPropEditor v-model="splitOptions" class="col-span-4 mt-4" :prop-def="propsDefs['splitOptions']" />
                <StdPropEditor v-model="semanticSplitModelName" class="col-span-4 mt-4" :prop-def="propsDefs['semanticSplitModelName']" />
                <StdPropEditor v-model="maxKbSearchDocuments" class="col-span-4 mt-4" :prop-def="propsDefs['maxKbSearchDocuments']" />
                <StdPropEditor v-model="searchByKeywordsEnabled" class="col-span-4 mt-4" :prop-def="propsDefs['searchByKeywordsEnabled']" />
                <StdPropEditor v-if="kbStore.contentPublishingEnabled" v-model="publishMethod" class="col-span-4 mt-4" :prop-def="propsDefs['publishMethod']" />
                <StdPropEditor v-if="publishMethod === 'ai'" v-model="contentPublisherModelName" class="col-span-4 mt-4" :prop-def="propsDefs['contentPublisherModelName']" />
                <StdPropEditor v-if="publishMethod === 'ai'" v-model="publishAnalyzerInstructions" class="col-span-4 mt-4" :prop-def="propsDefs['publishAnalyzerInstructions']" />
                <StdPropEditor v-model="contentFilterAI" class="col-span-4 mt-4" :prop-def="propsDefs['contentFilterAI']" />
                <StdPropEditor v-model="extractTextFromImages" class="col-span-4 mt-4" :prop-def="propsDefs['extractTextFromImages']" />
                <StdPropEditor v-model="purgeDeletedDocumentsEnabled" class="col-span-4 mt-4" :prop-def="propsDefs['purgeDeletedDocumentsEnabled']" />
                <StdPropEditor v-if="purgeDeletedDocumentsEnabled" v-model="purgeDeletedEnabledWarning" class="col-span-4 mt-4" :prop-def="propsDefs['purgeDeletedEnabledWarning']" />
                <StdPropEditor
                  v-if="settingsStore.salesforceIntegrationEnabled && salesforceHelpCenterSourceEnabled"
                  v-model="salesforceHelpCenterSourceRefreshInterval"
                  class="col-span-4 mt-4"
                  :prop-def="propsDefs['salesforceHelpCenterSourceRefreshInterval']"
                />
                <!-- [sv2] DO NOT CHECK settingsStore.zendeskIntegrationEnabled here. Loading from Zendesk HC can be done without enabling integration, via pulick API -->
                <StdPropEditor v-if="zendeskHelpCenterSourceEnabled" v-model="zendeskHelpCenterSourceRefreshInterval" class="col-span-4 mt-4" :prop-def="propsDefs['zendeskHelpCenterSourceRefreshInterval']" />

                <StdPropEditor
                  v-if="settingsStore.shopifyIntegrationEnabled && shopifyHelpCenterSourceEnabled"
                  v-model="shopifyHelpCenterSourceRefreshInterval"
                  class="col-span-4 mt-4"
                  :prop-def="propsDefs['shopifyHelpCenterSourceRefreshInterval']"
                />

                <StdPropEditor
                  v-if="settingsStore.kustomerIntegrationEnabled && kustomerHelpCenterSourceEnabled"
                  v-model="kustomerHelpCenterSourceRefreshInterval"
                  class="col-span-4 mt-4"
                  :prop-def="propsDefs['kustomerHelpCenterSourceRefreshInterval']"
                />
                <StdPropEditor
                  v-if="settingsStore.gorgiasIntegrationEnabled && gorgiasHelpCenterSourceEnabled"
                  v-model="gorgiasHelpCenterSourceRefreshInterval"
                  class="col-span-4 mt-4"
                  :prop-def="propsDefs['gorgiasHelpCenterSourceRefreshInterval']"
                />
                <StdPropEditor
                  v-if="settingsStore.intercomIntegrationEnabled && intercomHelpCenterSourceEnabled"
                  v-model="intercomHelpCenterSourceRefreshInterval"
                  class="col-span-4 mt-4"
                  :prop-def="propsDefs['intercomHelpCenterSourceRefreshInterval']"
                />
                <StdPropEditor
                  v-if="settingsStore.confluenceIntegrationEnabled && confluenceHelpCenterSourceEnabled"
                  v-model="confluenceHelpCenterSourceRefreshInterval"
                  class="col-span-4 mt-4"
                  :prop-def="propsDefs['confluenceHelpCenterSourceRefreshInterval']"
                />
                <StdPropEditor
                  v-if="settingsStore.guruIntegrationEnabled && guruHelpCenterSourceEnabled"
                  v-model="guruHelpCenterSourceRefreshInterval"
                  class="col-span-4 mt-4"
                  :prop-def="propsDefs['guruHelpCenterSourceRefreshInterval']"
                />
              </ToggleGroup>
            </div>
          </div>
        </div>
      </div>
    </div>
    <KbDialogDownloadZendeskHelpCenter ref="dialogDownloadZendeskHelpCenter"></KbDialogDownloadZendeskHelpCenter>
    <KbDialogDownloadSalesforceHelpCenter ref="dialogDownloadSalesforceHelpCenter"></KbDialogDownloadSalesforceHelpCenter>
    <KbDialogDownloadShopifyHelpCenter ref="dialogDownloadShopifyHelpCenter"></KbDialogDownloadShopifyHelpCenter>
    <KbDialogDownloadKustomerHelpCenter ref="dialogDownloadKustomerHelpCenter"></KbDialogDownloadKustomerHelpCenter>
    <KbDialogDownloadGorgiasHelpCenter ref="dialogDownloadGorgiasHelpCenter"></KbDialogDownloadGorgiasHelpCenter>
    <KbDialogDownloadIntercomHelpCenter ref="dialogDownloadIntercomHelpCenter"></KbDialogDownloadIntercomHelpCenter>
    <KbDialogDownloadConfluenceHelpCenter ref="dialogDownloadConfluenceHelpCenter"></KbDialogDownloadConfluenceHelpCenter>
    <KbDialogDownloadGuruHelpCenter ref="dialogDownloadGuruHelpCenter"></KbDialogDownloadGuruHelpCenter>
  </div>
</template>

<script setup>
  import { computed, ref, watch, onMounted, onActivated } from 'vue';
  import { useRoute } from 'vue-router';
  import { useKbStore } from '@/store/KbStore';
  import { useSettingsStore } from '@/store/SettingsStore';
  import { useToast } from 'vue-toastification';
  import ToggleGroup from '@/components/Toggles/ToggleGroup.vue';
  import InputTextarea from '@/components/Bots/editors/InputTextarea.vue';
  import { KB_TYPE_DEFS } from '@/spec/KbTypeDefs';
  import StdPropEditor from '@/components/Editors/StdPropEditor.vue';
  import StdTagInputEditor from '@/components/Editors/StdTagInputEditor.vue';
  import Button from 'primevue/button';
  import KbDialogDownloadZendeskHelpCenter from '@/components/KB/dialogs/KbDialogDownloadZendeskHelpCenter.vue';
  import KbDialogDownloadSalesforceHelpCenter from '@/components/KB/dialogs/KbDialogDownloadSalesforceHelpCenter.vue';
  import KbDialogDownloadShopifyHelpCenter from '@/components/KB/dialogs/KbDialogDownloadShopifyHelpCenter.vue';
  import KbDialogDownloadKustomerHelpCenter from '@/components/KB/dialogs/KbDialogDownloadKustomerHelpCenter.vue';
  import KbDialogDownloadGorgiasHelpCenter from '@/components/KB/dialogs/KbDialogDownloadGorgiasHelpCenter.vue';
  import KbDialogDownloadIntercomHelpCenter from '@/components/KB/dialogs/KbDialogDownloadIntercomHelpCenter.vue';
  import KbDialogDownloadConfluenceHelpCenter from '@/components/KB/dialogs/KbDialogDownloadConfluenceHelpCenter.vue';
  import KbDialogDownloadGuruHelpCenter from '@/components/KB/dialogs/KbDialogDownloadGuruHelpCenter.vue';
  import dayjs from 'dayjs';
  import MultiSelect from 'primevue/multiselect';
  import { useApi } from '@/hooks/useApi';

  const api = useApi();
  const route = useRoute();
  const showAdvanced = computed(() => 'advanced' in (route?.query || {}));
  const kbStore = useKbStore();
  const settingsStore = useSettingsStore();
  const toast = useToast();

  const dialogDownloadZendeskHelpCenter = ref(null);
  const dialogDownloadSalesforceHelpCenter = ref(null);
  const dialogDownloadShopifyHelpCenter = ref(null);
  const dialogDownloadKustomerHelpCenter = ref(null);
  const dialogDownloadGorgiasHelpCenter = ref(null);
  const dialogDownloadIntercomHelpCenter = ref(null);
  const dialogDownloadConfluenceHelpCenter = ref(null);
  const dialogDownloadGuruHelpCenter = ref(null);

  // todo show based on user role
  const advancedEnabled = ref(false);

  const propsDefs = KB_TYPE_DEFS['standard']?.propsByName || {};

  const specName = computed(kbStore.getKbSpecPropGetSet('name'));
  const specDescription = computed(kbStore.getKbSpecPropGetSet('description'));
  const specTags = computed(kbStore.getKbSpecPropGetSet('tags'));

  const webSourceEnabled = computed(kbStore.getKbSpecPropGetSet('webSourceEnabled'));
  const sitemaps = computed(kbStore.getKbSpecPropGetSet('sitemaps'));
  const urls = computed(kbStore.getKbSpecPropGetSet('urls'));

  const salesforceHelpCenterSourceEnabled = computed(kbStore.getKbSpecPropGetSet('salesforceHelpCenterSourceEnabled'));
  const salesforceHelpCenterSourceRefreshInterval = computed(kbStore.getKbSpecPropGetSet('salesforceHelpCenterSourceRefreshInterval'));
  const salesforceHelpCenterRecordTypes = computed(kbStore.getKbSpecPropGetSet('salesforceHelpCenterRecordTypes'));
  const hcRecordTypes = ref(['FAQ', 'Procedure']);
  const hcRecordTypesSelected = computed({
    get() {
      return (salesforceHelpCenterRecordTypes.value || '').split(', ').filter(Boolean);
    },
    set(val) {
      salesforceHelpCenterRecordTypes.value = val.filter(Boolean).join(', ');
    },
  });
  const salesforcePrivateArticlesEnabled = computed(kbStore.getKbSpecPropGetSet('salesforcePrivateArticlesEnabled'));

  const shopifyHelpCenterSourceEnabled = computed(kbStore.getKbSpecPropGetSet('shopifyHelpCenterSourceEnabled'));
  const shopifyHelpCenterSourceRefreshInterval = computed(kbStore.getKbSpecPropGetSet('shopifyHelpCenterSourceRefreshInterval'));
  const shopifyHelpCenterRecordTypes = computed(kbStore.getKbSpecPropGetSet('shopifyHelpCenterRecordTypes'));
  const shopifyRecordTypes = ref(['Articles', 'Products']);
  const shopifyRecordTypesSelected = computed({
    get() {
      return (shopifyHelpCenterRecordTypes.value || '').split(', ').filter(Boolean);
    },
    set(val) {
      shopifyHelpCenterRecordTypes.value = val.filter(Boolean).join(', ');
    },
  });
  const loadingShopifyProducts = ref(false);
  const shopifyProducts = ref([]);
  const shopifyProductMetafieldDefinitions = computed(kbStore.getKbSpecPropGetSet('shopifyProductMetafieldDefinitions'));
  const shopifyProductMetafieldDefinitionsSelected = computed({
    get() {
      return (shopifyProductMetafieldDefinitions.value || '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
        .map((val) => {
          const [namespace, key] = val.split('.');
          const item = shopifyProducts.value.find((opt) => opt.namespace === namespace && opt.key === key);
          return item ? item.name : null;
        })
        .filter(Boolean);
    },
    set(val) {
      shopifyProductMetafieldDefinitions.value = val
        .map((name) => {
          const item = shopifyProducts.value.find((opt) => opt.name === name);
          return item ? `${item.namespace}.${item.key}` : null;
        })
        .filter(Boolean)
        .join(',');
    },
  });
  const shopifyPrivateArticlesEnabled = computed(kbStore.getKbSpecPropGetSet('shopifyPrivateArticlesEnabled'));

  const zendeskHelpCenterSourceEnabled = computed(kbStore.getKbSpecPropGetSet('zendeskHelpCenterSourceEnabled'));
  const zendeskHelpCenterSourceRefreshInterval = computed(kbStore.getKbSpecPropGetSet('zendeskHelpCenterSourceRefreshInterval'));
  const zendeskHelpCentersSubdomains = computed(kbStore.getKbSpecPropGetSet('zendeskHelpCentersSubdomains'));
  const zendeskPrivateArticlesEnabled = computed(kbStore.getKbSpecPropGetSet('zendeskPrivateArticlesEnabled'));

  const kustomerHelpCenterSourceEnabled = computed(kbStore.getKbSpecPropGetSet('kustomerHelpCenterSourceEnabled'));
  const kustomerHelpCenterSourceRefreshInterval = computed(kbStore.getKbSpecPropGetSet('kustomerHelpCenterSourceRefreshInterval'));
  const kustomerPrivateArticlesEnabled = computed(kbStore.getKbSpecPropGetSet('kustomerPrivateArticlesEnabled'));

  const gorgiasHelpCenterSourceEnabled = computed(kbStore.getKbSpecPropGetSet('gorgiasHelpCenterSourceEnabled'));
  const gorgiasHelpCenterSourceRefreshInterval = computed(kbStore.getKbSpecPropGetSet('gorgiasHelpCenterSourceRefreshInterval'));
  const gorgiasHelpCentersSubdomains = computed(kbStore.getKbSpecPropGetSet('gorgiasHelpCentersSubdomains'));

  const intercomHelpCenterSourceEnabled = computed(kbStore.getKbSpecPropGetSet('intercomHelpCenterSourceEnabled'));
  const intercomHelpCenterSourceRefreshInterval = computed(kbStore.getKbSpecPropGetSet('intercomHelpCenterSourceRefreshInterval'));

  const confluenceHelpCenterSourceEnabled = computed(kbStore.getKbSpecPropGetSet('confluenceHelpCenterSourceEnabled'));
  const confluenceSpaceKeys = computed(kbStore.getKbSpecPropGetSet('confluenceSpaceKeys'));
  const confluenceLabels = computed(kbStore.getKbSpecPropGetSet('confluenceLabels'));
  const confluenceHelpCenterSourceRefreshInterval = computed(kbStore.getKbSpecPropGetSet('confluenceHelpCenterSourceRefreshInterval'));

  const guruHelpCenterSourceEnabled = computed(kbStore.getKbSpecPropGetSet('guruHelpCenterSourceEnabled'));
  const guruHelpCentersTags = computed(kbStore.getKbSpecPropGetSet('guruHelpCentersTags'));
  const guruPrivateArticlesEnabled = computed(kbStore.getKbSpecPropGetSet('guruPrivateArticlesEnabled'));
  const guruHelpCenterSourceRefreshInterval = computed(kbStore.getKbSpecPropGetSet('guruHelpCenterSourceRefreshInterval'));

  const automatedUpdatesEnabled = computed(kbStore.getKbSpecPropGetSet('automatedUpdatesEnabled'));

  const mode = computed(kbStore.getKbSpecPropGetSet('mode'));
  const embeddingModel = computed(kbStore.getKbSpecPropGetSet('embeddingModel'));
  const vectorStore = computed(kbStore.getKbSpecPropGetSet('vectorStore'));
  const splitSizeThreshold = computed(kbStore.getKbSpecPropGetSet('splitSizeThreshold'));
  const splitOptions = computed(kbStore.getKbSpecPropGetSet('splitOptions'));
  const semanticSplitModelName = computed(kbStore.getKbSpecPropGetSet('semanticSplitModelName'));
  const maxKbSearchDocuments = computed(kbStore.getKbSpecPropGetSet('maxKbSearchDocuments'));
  const searchByKeywordsEnabled = computed(kbStore.getKbSpecPropGetSet('searchByKeywordsEnabled'));
  const purgeDeletedDocumentsEnabled = computed(kbStore.getKbSpecPropGetSet('purgeDeletedDocumentsEnabled'));
  const extractTextFromImages = computed(kbStore.getKbSpecPropGetSet('extractTextFromImages'));
  const publishMethod = computed(kbStore.getKbSpecPropGetSet('publishMethod'));
  const contentPublisherModelName = computed(kbStore.getKbSpecPropGetSet('contentPublisherModelName'));
  const publishAnalyzerInstructions = computed(kbStore.getKbSpecPropGetSet('publishAnalyzerInstructions'));
  const contentFilterAI = computed(kbStore.getKbSpecPropGetSet('contentFilterAI'));

  function loadShopifyProductsData() {
    loadingShopifyProducts.value = true;
    api.shopifyGetAllProductMetafieldDefinitions({}).then(({ data }) => {
      loadingShopifyProducts.value = false;
      shopifyProducts.value = data;
    });
  }

  function triggerLoadShopify() {
    if (shopifyRecordTypesSelected.value.includes('Products')) {
      loadShopifyProductsData();
    }
  }

  watch(shopifyRecordTypesSelected, (val) => {
    if (val.includes('Products')) {
      loadShopifyProductsData();
    }
  });
  onMounted(triggerLoadShopify);
  onActivated(triggerLoadShopify);

  function onKbAddTag(tag) {
    kbStore.addTagToKb(tag);
  }

  async function checkSaveKb() {
    if (!kbStore.kbIsChanged) {
      return true;
    }

    const saveRes = await kbStore.saveKb();
    if (!saveRes) {
      toast.error(`Error saving KB`);
      return false;
    }
    toast.success('KB saved successfully');
    return true;
  }

  async function onDownloadZendeskHelpCenters(reloadAll = false) {
    const checkSaveRes = await checkSaveKb();
    if (!checkSaveRes) {
      return;
    }
    const kb = kbStore.getKb(kbStore.kbId);
    const lastUpdate = kb?.zendeskHelpCenterLastUpdated || null;
    const lastUpdateDate = lastUpdate ? dayjs(lastUpdate).format() : null;

    const subdomains = (kbStore.kbSpec?.zendeskHelpCentersSubdomains || '').split(',').map((x) => x.trim());
    dialogDownloadZendeskHelpCenter.value.start(reloadAll, subdomains, lastUpdateDate);
  }

  async function onDownloadSalesforceHelpCenters(reloadAll = false) {
    const checkSaveRes = await checkSaveKb();
    if (!checkSaveRes) {
      return;
    }
    const kb = kbStore.getKb(kbStore.kbId);
    const lastUpdate = kb?.salesforceHelpCenterLastUpdated || null;
    const lastUpdateDate = lastUpdate ? dayjs(lastUpdate).format() : null;

    const recordTypes = (kbStore.kbSpec?.salesforceHelpCenterRecordTypes || '').split(',').map((x) => x.trim());
    dialogDownloadSalesforceHelpCenter.value.start(reloadAll, recordTypes, lastUpdateDate);
  }

  async function onDownloadShopifyHelpCenters(reloadAll = false) {
    const checkSaveRes = await checkSaveKb();
    if (!checkSaveRes) {
      return;
    }
    const kb = kbStore.getKb(kbStore.kbId);
    const lastUpdate = kb?.shopifyHelpCenterLastUpdated || null;
    const lastUpdateDate = lastUpdate ? dayjs(lastUpdate).format() : null;

    const recordTypes = (kbStore.kbSpec?.shopifyHelpCenterRecordTypes || '').split(',').map((x) => x.trim());
    const products = shopifyProductMetafieldDefinitionsSelected.value;
    dialogDownloadShopifyHelpCenter.value.start(reloadAll, recordTypes, lastUpdateDate, products);
  }

  async function onDownloadKustomerHelpCenters(reloadAll = false) {
    const checkSaveRes = await checkSaveKb();
    if (!checkSaveRes) {
      return;
    }
    const kb = kbStore.getKb(kbStore.kbId);
    const lastUpdate = kb?.kustomerHelpCenterLastUpdated || null;
    const lastUpdateDate = lastUpdate ? dayjs(lastUpdate).format() : null;

    dialogDownloadKustomerHelpCenter.value.start(reloadAll, lastUpdateDate);
  }

  async function onDownloadGorgiasHelpCenters(reloadAll = false) {
    const checkSaveRes = await checkSaveKb();
    if (!checkSaveRes) {
      return;
    }
    const kb = kbStore.getKb(kbStore.kbId);
    const lastUpdate = kb?.gorgiasHelpCenterLastUpdated || null;
    const lastUpdateDate = lastUpdate ? dayjs(lastUpdate).format() : null;

    const subdomains = (kbStore.kbSpec?.gorgiasHelpCentersSubdomains || '').split(',').map((x) => x.trim());
    dialogDownloadGorgiasHelpCenter.value.start(reloadAll, subdomains, lastUpdateDate);
  }

  async function onDownloadIntercomHelpCenters(reloadAll = false) {
    const checkSaveRes = await checkSaveKb();
    if (!checkSaveRes) {
      return;
    }

    const kb = kbStore.getKb(kbStore.kbId);
    const lastUpdate = kb?.intercomHelpCenterLastUpdated || null;
    const lastUpdateDate = lastUpdate ? dayjs(lastUpdate).format() : null;

    dialogDownloadIntercomHelpCenter.value.start(reloadAll, lastUpdateDate);
  }

  async function onDownloadConfluenceHelpCenters(reloadAll = false) {
    const checkSaveRes = await checkSaveKb();
    if (!checkSaveRes) {
      return;
    }

    const kb = kbStore.getKb(kbStore.kbId);
    const lastUpdate = kb?.confluenceHelpCenterLastUpdated || null;
    const lastUpdateDate = lastUpdate ? dayjs(lastUpdate).format() : null;

    dialogDownloadConfluenceHelpCenter.value.start(reloadAll, lastUpdateDate);
  }

  async function onDownloadGuruHelpCenters(reloadAll = false) {
    const checkSaveRes = await checkSaveKb();
    if (!checkSaveRes) {
      return;
    }

    const kb = kbStore.getKb(kbStore.kbId);
    const lastUpdate = kb?.guruHelpCenterLastUpdated || null;
    const lastUpdateDate = lastUpdate ? dayjs(lastUpdate).format() : null;

    dialogDownloadGuruHelpCenter.value.start(reloadAll, lastUpdateDate);
  }
</script>
